import AssetDetailCard from "components/card/AssetDetailsCard";
import FileRow from "components/file-components/file-row";
import EditIcon from "components/icon/EditIcon";
import { RespondBackAssetRecoveryPopup } from "components/pop-up/RespondBackAssetRecovery";
import { RespondBackPopup } from "components/pop-up/RespondBackPopup";
import ReadMoreText from "components/ReadMoreText";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import TerminationContainer from "pages/AssetRecovery/Termination/TerminationContainer";
import React, { useState } from "react";
import { updateAssetReferral } from "services/assetRecoveryService";
import {
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
  Referral,
  Roles,
} from "types";
import { getAvatarBgColor, getInitials, hasRole } from "utils/helper";

interface DecisionDetailsProps {
  // Coordinator Decision
  document_details: File[];
  document_summary: string;
  document_date: string | null;
  is_prohabition_request?: boolean;
  // Prosecutor Decision (Prohabition)
  asset_recovery_request_date?: string | null;
  prosecutor_decision_on_asset_recovery?: string | null;
  asset_reovery_charge?: string | null;
  asset_recovery_request_document: File[];
  prohabition_request_document: File[];
  prohabition_request_summary: string;
  prohabition_request_date: string | null;

  // Assigned prosecutor and statuses
  assigned_prosecutor: string;
  forward_status: string;
  action_status: string;
  referralId: string;
  // File History
  file_histories: Array<{
    id: string;
    file_type: string;
    old_file: string | null;
    new_file: string;
    old_summary: string | null;
    new_summary: string;
    old_date: string | null;
    new_date: string | null;
    changed_at: string;
  }> | null;
  fileRowProps: any;
  onDecsionCreated: (res: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  handleAddAssetDecisionClick?: () => void;
  handleAddAssetRecoveryDecisionClick?: () => void;
  referral: Referral;
  handleCreateTerminationRequestClick?: () => void;
}

export const DecisionDetailsCard: React.FC<DecisionDetailsProps> = ({
  // Coordinator
  document_details,
  document_summary,
  document_date,
  is_prohabition_request,
  // Prosecutor
  prohabition_request_document,
  prohabition_request_summary,
  prohabition_request_date,

  // Other
  assigned_prosecutor,
  file_histories,
  action_status,
  referralId,
  onDecsionCreated,
  forward_status,
  setActiveTab,
  fileRowProps,
  asset_recovery_request_document,
  asset_recovery_request_date,
  asset_reovery_charge,
  prosecutor_decision_on_asset_recovery,
  handleAddAssetDecisionClick,
  handleAddAssetRecoveryDecisionClick,
  referral,
  handleCreateTerminationRequestClick,
}) => {
  const [isForwarding, setIsForwarding] = useState(false);
  const [status, setStatus] = useState(action_status);
  const { loginData } = useAuth();
  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const [isRespondBackOpen, setIsRespondBackOpen] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const initials = getInitials(assigned_prosecutor);
  const bgColor = getAvatarBgColor(assigned_prosecutor);
  const [isRespondBackAssetRecoveryOpen, setIsRespondBackAssetRecoveryOpen] =
    useState(false);
  const handleForwardToCoordinator = async () => {
    try {
      setIsForwarding(true);

      const formData = new FormData();
      if (
        action_status === AssetReferralActionStatusEnum?.PROHIBITION_ON_REVISION
      ) {
        formData.append(
          "action_status",
          AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED,
        );
        setStatus(AssetReferralActionStatusEnum?.PROHIBITION_ON_UPDATED);
      } else if (
        action_status === AssetReferralActionStatusEnum?.INVESTIGATION_ONGOING
      ) {
        formData.append(
          "action_status",
          AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED,
        );
        setStatus(AssetReferralActionStatusEnum?.PROHIBITION_REQUESTED);
      }
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR,
      );
      const response = await updateAssetReferral(referralId, formData);
      showTooltip(t("referral_forwarded_successfully"), "success");

      if (onDecsionCreated && response) {
        onDecsionCreated(response);
        setActiveTab?.("Decision");
      }
    } catch (error) {
      console.error("Error forwarding to coordinator:", error);
      showTooltip(t("failed_to_forward_to_coordinator"), "error");
    } finally {
      setIsForwarding(false);
    }
  };

  const hasProhibitionData = Boolean(
    prohabition_request_date ||
    prohabition_request_summary ||
    (prohabition_request_document && prohabition_request_document.length > 0),
  );
  const hasAssetRecoveryData = Boolean(
    asset_recovery_request_date || prosecutor_decision_on_asset_recovery,
    // asset_recovery_request_document
  );
  const hasTerminationData =
    referral?.termination_reason ||
    referral?.termination_date ||
    (referral?.termination_order_documents &&
      referral.termination_order_documents.length > 0);
  const handleProsecutorApproveDecision = async () => {
    try {
      setIsForwarding(true);

      const formData = new FormData();
      formData.append(
        "action_status",
        AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED,
      );
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_REGISTRAR,
      );

      const response = await updateAssetReferral(referralId, formData);
      if (onDecsionCreated && response) {
        onDecsionCreated(response);
        setActiveTab?.("Decision");
      }
      setStatus(AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED);
      showTooltip(t("prohibition_request_approved_successfully"), "success");
    } catch (error) {
      console.error("Error approving prohibition:", error);
      showTooltip(t("failed_to_approve_prohibition"), "error");
    } finally {
      setIsForwarding(false);
    }
  };
  const handleForwardAssetREcoveryDecision = async () => {
    try {
      setIsForwarding(true);

      const formData = new FormData();
      formData.append("action_status", "Asset Recovery Request Approved");
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_REGISTRAR,
      );

      const response = await updateAssetReferral(referralId, formData);
      if (onDecsionCreated && response) {
        onDecsionCreated(response);
        setActiveTab?.("Decision");
      }
      showTooltip(t("asset_recovery_request_approved_successfully"), "success");
    } catch (error) {
      console.error("Error approving prohibition:", error);
      showTooltip(t("failed_to_approve_prohibition"), "error");
    } finally {
      setIsForwarding(false);
    }
  };

  return (
    <>
      <div className=" bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8 w-full mr-3">
        {hasAssetRecoveryData && (
          <>
            <div className="shadow-md rounded-md p-4">
              <div className=" mb-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Asset Recovery
                  </h2>
                  {asset_recovery_request_date && (
                    <span className="text-sm text-gray-600 font-bold">
                      {asset_recovery_request_date
                        ? new Date(
                            asset_recovery_request_date,
                          ).toLocaleDateString()
                        : "—"}
                    </span>
                  )}
                  {isAssetProsecutor &&
                    (action_status ===
                      AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_REVISION ||
                      action_status ===
                        AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUESTED) &&
                    hasProhibitionData && (
                      <button
                        type="button"
                        onClick={handleAddAssetRecoveryDecisionClick}
                        className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors"
                      >
                        <EditIcon />
                        Edit
                      </button>
                    )}
                </div>
                <div className="flex justify-between items-start flex-col">
                  {" "}
                  <div className="flex flex-col">
                    {/* <label className="mb-2 text-base font-semibold text-gray-800">
                    Summary
                  </label> */}
                    <div className="py-2 ">
                      {prosecutor_decision_on_asset_recovery ? (
                        <p className="text-gray-700 whitespace-pre-wrap">
                          <ReadMoreText
                            text={prosecutor_decision_on_asset_recovery}
                            maxLines={3}
                          />
                        </p>
                      ) : (
                        <p className="text-gray-400">No summary provided</p>
                      )}
                    </div>
                    <div className="flex text-[15px] items-start flex-col">
                      <p className="font-semibold mr-2 ">
                        Asset Recovery Charge:
                      </p>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        <ReadMoreText
                          text={asset_reovery_charge || ""}
                          maxLines={3}
                        />
                      </p>
                    </div>
                  </div>
                  {asset_recovery_request_document &&
                    asset_recovery_request_document?.length > 0 &&
                    asset_recovery_request_document?.map((file: any, index) => (
                      <FileRow
                        file={file}
                        {...fileRowProps}
                        w="w-full"
                        key={file.document_uuid || index}
                      />
                    ))}
                </div>
              </div>
              {isAssetCoordinator &&
                (action_status?.toLowerCase() ===
                  AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUESTED?.toLocaleLowerCase() ||
                  action_status?.toLowerCase() ===
                    AssetReferralActionStatusEnum?.ASSET_RECOVERY_UPDATED?.toLocaleLowerCase()) && (
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={handleForwardAssetREcoveryDecision}
                      // disabled={isForwarding}
                      className="px-4 py-2 text-sm font-medium bg-black text-white rounded-md hover:bg-gray-700 disabled:opacity-60"
                    >
                      Forward To Registrar
                    </button>

                    <button
                      onClick={() => setIsRespondBackAssetRecoveryOpen(true)}
                      // disabled={isForwarding}
                      className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-60"
                    >
                      Respond Back
                    </button>
                  </div>
                )}
              {referral?.assets?.some(
                (asset) =>
                  asset?.prohabition_court_verdict?.toLocaleLowerCase() ===
                  "prohabited",
              ) && (
                <div className="mb-6">
                  <label className="block text-[15px] font-medium text-gray-700 mb-1">
                    Prohibited Assets
                  </label>
                  <div className="flex flex-wrap gap-2 flex-col">
                    {referral?.assets
                      ?.filter(
                        (asset) =>
                          asset?.prohabition_court_verdict?.toLocaleLowerCase() ===
                          "prohabited",
                      )
                      .map((asset) => (
                        <AssetDetailCard asset={asset} key={asset.id} />
                      ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ───────────────────────────── Coordinator Decision ───────────────────────────── */}
        {(action_status?.toLowerCase() === "closed by cordinator" ||
          action_status?.toLowerCase() === "closed by prosecutor") && (
          <div className="shadow-md rounded-md p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Decision
                </h2>
              </div>
              <span className="text-sm text-gray-600 font-semibold">
                {document_date
                  ? new Date(document_date).toLocaleDateString()
                  : "—"}
              </span>
            </div>

            {document_summary ? (
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm mb-4">
                <ReadMoreText text={document_summary} maxLines={3} />
              </p>
            ) : (
              <p className="text-gray-400">No summary provided</p>
            )}

            {document_details &&
              document_details.length > 0 &&
              document_details?.map((file: any, index) => (
                <FileRow
                  file={file}
                  {...fileRowProps}
                  key={file.document_uuid || index}
                />
              ))}
          </div>
        )}
        {assigned_prosecutor && (
          <div className="flex gap-2  mt-4 flex-col">
            <p className="text-base font-semibold text-gray-800">
              Assigned Prosecutor
            </p>
            <div className="flex items-center gap-2 p-3 border rounded-lg">
              {" "}
              <div
                className={`w-10 h-10 rounded-full flex items-center
                         justify-center text-white text-lg font-semibold flex-shrink-0 mr-3 ${bgColor}`}
              >
                {initials}
              </div>
              <div>
                <p className="font-medium text-gray-800">
                  {assigned_prosecutor || "Prosecutor Name"}
                </p>
                <p className="text-sm text-gray-500">Prosecutor</p>
              </div>
            </div>
          </div>
        )}

        {hasTerminationData && (
          <TerminationContainer
            referralId={referralId as string}
            referral={referral as Referral}
            setActiveTab={setActiveTab}
            onTerminationCreated={onDecsionCreated}
            handleCreateTerminationRequestClick={
              handleCreateTerminationRequestClick
            }
          />
        )}
      </div>
      <RespondBackPopup
        isOpen={isRespondBackOpen}
        onClose={() => setIsRespondBackOpen(false)}
        referralId={referralId}
        onNewDataAdded={onDecsionCreated}
        setActiveTab={setActiveTab}
      />
      <RespondBackAssetRecoveryPopup
        isOpen={isRespondBackAssetRecoveryOpen}
        onClose={() => setIsRespondBackAssetRecoveryOpen(false)}
        referralId={referralId}
        onNewDataAdded={onDecsionCreated}
        setActiveTab={setActiveTab}
      />
    </>
  );
};
